import _ from "lodash";
import React, { useMemo } from "react";
import classes from "./MYPProjectYearGroupStep.scss";
import { useI18n } from "Hooks";

import {
  getFilteredYearGroupsMemoize,
  getFilteredSubjectListMemoize,
} from "MyCalendar/modules/Utils";

import { CardSelection, SelectDropdown } from "@toddle-edu/ds-web";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";

const styles = {
  cardContainerStyle: {
    minWidth: "300px",
  },
};

const MYPProjectYearGroupStep = props => {
  const {
    disabled,
    tagList,
    projectGroups,
    onUpdateEventDetails,
    eventDetails,
    testId,
  } = props;

  const {
    tag: selectedTag,
    yearGroup: selectedYearGroup,
    subject: selectedSubject,
    component: selectedComponent,
  } = eventDetails;

  const isIAEATag =
    selectedTag == MY_CALENDAR_CONSTANTS.INTERNAL_EXTERNAL_ASSESSMENT;

  const { t } = useI18n(["common", "project"]);

  const onCardSelectionClick = option => {
    onUpdateEventDetails({
      key: "tag",
      value: _.first(option),
    });
  };

  const onYearGroupSelection = ({ value }) => {
    onUpdateEventDetails({
      key: "yearGroup",
      value: value,
    });
  };

  const onSubjectSelection = ({ value }) => {
    onUpdateEventDetails({
      key: "subject",
      value: value,
    });
  };

  const onComponentSelection = ({ value }) => {
    onUpdateEventDetails({
      key: "component",
      value: value,
    });
  };

  const getProjectCards = () => {
    return _.map(tagList, tag => {
      const { label: title, value } = tag;
      return (
        <CardSelection
          selectedValues={[selectedTag]}
          title={title}
          key={value}
          type={"radio"}
          value={value}
          style={styles.cardContainerStyle}
          onCardSelectionClick={onCardSelectionClick}
          disabled={disabled}
          testId={`${testId}-projectCard-${value}`}
        />
      );
    });
  };

  const yearGroupList = getFilteredYearGroupsMemoize({
    projectGroups,
    tag: _.find(tagList, { value: selectedTag }),
    isEventTypeSchoolCalendar: false,
  });

  const filteredSubjectList = getFilteredSubjectListMemoize({
    projectGroups,
    yearGroupId: _.find(yearGroupList, { value: selectedYearGroup })?.id,
  });

  const subjectListOptions = _.map(
    filteredSubjectList,
    (subject, subjectId) => {
      return {
        label: subject?.subjectName,
        value: subjectId,
        subjectComponentList: subject?.subjectComponentList,
      };
    }
  );

  const showSubjectSelector = isIAEATag && selectedYearGroup;

  const showIAComponent = isIAEATag && selectedSubject && selectedYearGroup;

  const componentOptions = useMemo(() => {
    return _.get(
      _.find(subjectListOptions, { value: selectedSubject }),
      "subjectComponentList",
      []
    );
  }, [selectedSubject, selectedYearGroup]);

  return (
    <div className={classes.container}>
      <div className={classes.projectContainer}>
        <div className={"text-label"}>{t("project:choose_a_project")}</div>
        <div className={classes.cardContainer}>{getProjectCards()}</div>
      </div>
      <div className={classes.yearGroupContainer}>
        <div className={"text-label"}>{t("common:year_group")}</div>
        <SelectDropdown
          options={yearGroupList}
          onChange={onYearGroupSelection}
          value={_.find(yearGroupList, { value: selectedYearGroup }) ?? null}
          isDisabled={disabled}
          isClearable={true}
        />
      </div>
      {showSubjectSelector && (
        <div className={classes.yearGroupContainer}>
          <div className={"text-label"}>{t("common:subject")}</div>
          <SelectDropdown
            options={subjectListOptions}
            onChange={onSubjectSelection}
            value={
              _.find(subjectListOptions, { value: selectedSubject }) ?? null
            }
            isDisabled={disabled}
            isClearable={true}
          />
        </div>
      )}
      {showIAComponent && (
        <div className={classes.yearGroupContainer}>
          <div className={"text-label"}>{t("common:component")}</div>
          <SelectDropdown
            options={componentOptions}
            onChange={onComponentSelection}
            value={
              _.find(componentOptions, { value: selectedComponent }) ?? null
            }
            isDisabled={disabled}
            isClearable={true}
          />
        </div>
      )}
    </div>
  );
};

export default MYPProjectYearGroupStep;
